import { useStyles } from './styles';
import Layout from 'components/Layout';
import { Chip, CircularIndeterminate, DateFormattedUtils, PreviewExperiencer, Title } from '@utriper/core-ui';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Divider, Grid2, Fade, IconButton, Paper, Typography } from '@mui/material';
import { ArrowBack, Check, Close, Edit } from '@mui/icons-material';
import { Cost, formatCurrency } from '@utriper/core';
import { PreviewService } from 'core/api/services/preview';
import { useCosts } from 'core/contexts/cost';
import { useEffect } from 'react';

const CostPage = () => {
    const classes = useStyles();
    const { t: translate, i18n } = useTranslation();
    const { loading, cost, getCost } = useCosts();
    const history = useHistory();
    const { id } = useParams();
    const costId = parseInt(id);
    const TIMEOUT = 300;

    const renderCostTypeColor = (type) => {
        switch (type) {
            case Cost.IDS.system:
                return '#50c878';
            case Cost.IDS.user:
                return '#e4954b';
            default:
                return '#137dc5';
        }
    };

    const renderDate = (date) =>
        DateFormattedUtils(date, i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        });

    const onClickEdit = () => {
        history.push(`/cost/${costId}/edit`);
    };

    const onClickToList = () => {
        history.push(`/cost`);
    };

    useEffect(() => {
        if (costId) {
            getCost(costId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costId]);

    return (
        <Layout contentClassName={classes.content}>
            {/* Header */}
            <Grid2 container justifyContent="space-between" alignItems="center" direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Grid2 container alignItems="center" spacing={2} sx={{ mb: { xs: 2, sm: 0 } }}>
                    <Grid2>
                        <IconButton sx={{ p: { xs: 1, sm: 2 } }} onClick={onClickToList}>
                            <ArrowBack />
                        </IconButton>
                    </Grid2>

                    <Grid2 display="flex" flexDirection="column" justifyContent="center" alignItems="flex-start">
                        <Title value={loading ? translate('loading') : cost?.name ? cost.name : translate('cost.not_found')} />
                        {cost?.name && <Typography fontSize={{ xs: '0.9rem', sm: '1rem' }}>{`ID: ${cost?.id || 0}`}</Typography>}
                    </Grid2>
                </Grid2>

                {cost?.name && (
                    <Grid2>
                        <Button
                            variant="contained"
                            startIcon={<Edit />}
                            onClick={onClickEdit}
                            sx={{
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1, sm: 2 },
                            }}>
                            {translate('cost.edit_button')}
                        </Button>
                    </Grid2>
                )}
            </Grid2>

            {/* Content */}
            {loading ? (
                <CircularIndeterminate />
            ) : cost?.name ? (
                <Grid2 container justifyContent="center" alignItems="flex-start" direction={{ xs: 'column', sm: 'row' }} width="100%" spacing={2} p={8}>
                    <Fade in={true} timeout={TIMEOUT}>
                        <Grid2 size={{ xs: 12, md: 8.5 }}>
                            <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', padding: 3, borderRadius: 2, boxShadow: 'var(--shadow-card)' }}>
                                <Typography fontSize="1.2em" fontWeight="bold">
                                    {translate('cost.main_info.title')}
                                </Typography>
                                <Grid2 container justifyContent="flex-start" alignItems={{ xs: 'flex-start', sm: 'center' }} width="100%" direction={{ xs: 'column', sm: 'row' }} spacing={3} my={4}>
                                    <Grid2 container flexDirection="column" spacing={2} justifyContent="center" alignItems="flex-start">
                                        <Typography>{translate('cost.main_info.name_label')}</Typography>
                                        <Typography fontSize="1em" fontWeight="bold">
                                            {cost?.name}
                                        </Typography>
                                    </Grid2>
                                    {cost?.experiencerId && (
                                        <Grid2 container flexDirection="column" spacing={2} justifyContent="center" alignItems="flex-start">
                                            <Typography>{translate('cost.main_info.experiencer_label')}</Typography>
                                            <PreviewExperiencer userId={cost.experiencerId} label="name" previewService={PreviewService} />
                                        </Grid2>
                                    )}
                                    <Grid2 container flexDirection="column" spacing={2} justifyContent="center" alignItems="flex-start">
                                        <Typography>{translate('cost.main_info.type_label')}</Typography>
                                        <Chip sx={{ color: renderCostTypeColor(cost?.type), fontWeight: 'bold' }} label={Cost.getTranslatedName(Cost.types, cost?.type, i18n.language)} />
                                    </Grid2>
                                    <Grid2 container flexDirection="column" spacing={2} justifyContent="center" alignItems="flex-start">
                                        <Typography>{translate('cost.main_info.is_system_label')}</Typography>
                                        <Grid2 container justifyContent="center" alignItems="center" spacing={1}>
                                            {cost?.isSystem ? <Check sx={{ color: '#50c878' }} /> : <Close />}
                                            <Typography fontSize="1em" fontWeight="bold" color={cost?.isSystem ? '#50c878' : 'primary'}>
                                                {cost?.isSystem ? translate('yes') : translate('no')}
                                            </Typography>
                                        </Grid2>
                                    </Grid2>
                                </Grid2>
                                <Grid2 container flexDirection="column" spacing={2} justifyContent="center" alignItems="flex-start" width="100%" sx={{ padding: 3, backgroundColor: '#f7f4ff', borderRadius: 3 }}>
                                    <Typography>{translate('cost.main_info.cost_value')}</Typography>
                                    {cost?.defaultPercentage ? (
                                        <>
                                            <Typography fontSize="1.5em" fontWeight="bold">{`${cost.defaultPercentage}%`}</Typography>
                                            <Typography>{translate('cost.main_info.percentage')}</Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography fontSize="1.5em" fontWeight="bold">
                                                {formatCurrency(cost?.defaultPrice, cost?.defaultCurrency, i18n.language)}
                                            </Typography>
                                            <Typography>{translate('cost.main_info.fixed_price')}</Typography>
                                        </>
                                    )}
                                </Grid2>
                            </Paper>
                        </Grid2>
                    </Fade>

                    <Fade in={true} timeout={TIMEOUT}>
                        <Grid2 size={{ xs: 12, sm: 3.5 }}>
                            <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', padding: 3, borderRadius: 2, boxShadow: 'var(--shadow-card)' }}>
                                <Typography fontSize="1.2em" fontWeight="bold" mb={2}>
                                    {translate('cost.info_temp.title')}
                                </Typography>
                                <Grid2 container flexDirection="column" spacing={1} justifyContent="center" alignItems="flex-start" width="100%" py={1}>
                                    <Typography>{translate('cost.info_temp.created')}</Typography>
                                    <Typography fontSize="1em" fontWeight="bold">
                                        {renderDate(cost?.createdOn)}
                                    </Typography>
                                    <Typography>{`${translate('cost.info_temp.created_by')} ${cost?.createdBy}`}</Typography>
                                </Grid2>
                                {cost?.updatedOn && (
                                    <>
                                        <Divider sx={{ marginY: 2 }} flexItem />
                                        <Grid2 container flexDirection="column" spacing={1} justifyContent="center" alignItems="flex-start" width="100%" py={1}>
                                            <Typography>{translate('cost.info_temp.last_update')}</Typography>
                                            <Typography fontSize="1em" fontWeight="bold">
                                                {renderDate(cost?.updatedOn)}
                                            </Typography>
                                            <Typography>{`${translate('cost.info_temp.updated_by')} ${cost?.updatedBy}`}</Typography>
                                        </Grid2>
                                    </>
                                )}
                            </Paper>
                        </Grid2>
                    </Fade>
                </Grid2>
            ) : (
                <Grid2 container justifyContent="center" alignItems="flex-start" direction={{ xs: 'column', sm: 'row' }} width="100%" spacing={2} p={8}>
                    <Fade in={true} timeout={TIMEOUT}>
                        <Grid2 size={6}>
                            <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 3, borderRadius: 2, boxShadow: 'var(--shadow-card)' }}>
                                <Typography fontSize="1em" sx={{ my: 2 }}>
                                    {translate('cost.not_exists')}
                                </Typography>
                                <Button onClick={onClickToList} variant="contained" sx={{ my: 1, p: 2 }}>
                                    {translate('cost.return_table')}
                                </Button>
                            </Paper>
                        </Grid2>
                    </Fade>
                </Grid2>
            )}
        </Layout>
    );
};

export default CostPage;
